import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { LineChart, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter } from 'lucide-react';

import {
    fetchPFSSFinancialYears, fetchProjectFinancialStatusSummary, clearPFSSReport,
    selectPFSSYears, selectPFSSYearsLoading, selectPFSSReport, selectPFSSReportLoading, selectPFSSReportError,
} from '../../slices/ReportsSlice/projectFinancialStatusSummarySlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const NUMERIC_KEYS = [
    'BASIC_CURRENTYEAR', 'BASIC_UPTOPREVIOUSYEAR', 'BASIC_CUMULATIVE',
    'NETRECIEPT_CURRENTYEAR', 'NETRECIEPT_UPTOPREVIOUSYEAR', 'NETRECIEPT_CUMULATIVE',
    'TDS_CURRENTYEAR', 'TDS_UPTOPREVIOUSYEAR', 'TDS_CUMULATIVE',
    'STOCKTRANSFER_CURRENTYEAR', 'STOCKTRANSFER_UPTOPREVIOUSYEAR', 'STOCKTRANSFER_CUMULATIVE',
    'DIFFERENCEINVOICEVALUE', 'CreditNote', 'DebitNote', 'BadDebts',
];

const fmt = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const NumCell = ({ value, className = '' }) => {
    const n = parseFloat(value) || 0;
    return (
        <td className={`px-2 py-1 text-xs text-right whitespace-nowrap ${n < 0 ? 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300' : 'text-gray-700 dark:text-gray-200'} ${className}`}>
            {fmt(value)}
        </td>
    );
};

const ProjectFinancialStatusSummary = () => {
    const dispatch = useDispatch();
    const years = useSelector(selectPFSSYears);
    const yearsLoading = useSelector(selectPFSSYearsLoading);
    const report = useSelector(selectPFSSReport);
    const reportLoading = useSelector(selectPFSSReportLoading);
    const reportError = useSelector(selectPFSSReportError);

    const [ccType, setCcType] = useState(''); // 'Active CCs' | 'Closed CCs'
    const [year, setYear] = useState('');
    const [ranParams, setRanParams] = useState(null);

    useEffect(() => { dispatch(fetchPFSSFinancialYears()); }, [dispatch]);
    useEffect(() => { if (reportError) toast.error(reportError); }, [reportError]);

    const rows = report?.ProjectFinancialStatusSummaryData || [];

    const totals = useMemo(() => rows.reduce((acc, r) => {
        NUMERIC_KEYS.forEach((k) => { acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0); });
        return acc;
    }, {}), [rows]);

    const handleReset = () => {
        setCcType(''); setYear(''); setRanParams(null);
        dispatch(clearPFSSReport());
    };

    const runReport = () => {
        if (!ccType) { toast.warning(`Select a Cost Centre Type.`); return; }
        if (!year) { toast.warning('Select a Financial Year.'); return; }
        const params = { CCType: ccType, Fyear: year };
        setRanParams(params);
        dispatch(fetchProjectFinancialStatusSummary(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchProjectFinancialStatusSummary(ranParams)); };

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = [...rows, { CCCode: '', CCName: 'TOTAL', ...totals }].map((r) => ({
            'CC Code': r.CCCode, 'Project/Job Description': r.CCName,
            'Basic Invoice - Current Year': r.BASIC_CURRENTYEAR, 'Basic Invoice - Up To Previous Year': r.BASIC_UPTOPREVIOUSYEAR, 'Basic Invoice - Cumulative': r.BASIC_CUMULATIVE,
            'Net Receipts - Current Year': r.NETRECIEPT_CURRENTYEAR, 'Net Receipts - Up To Previous Year': r.NETRECIEPT_UPTOPREVIOUSYEAR, 'Net Receipts - Cumulative': r.NETRECIEPT_CUMULATIVE,
            'TDS - Current Year': r.TDS_CURRENTYEAR, 'TDS - Up To Previous Year': r.TDS_UPTOPREVIOUSYEAR, 'TDS - Cumulative': r.TDS_CUMULATIVE,
            'Stock Transfer - Current Year': r.STOCKTRANSFER_CURRENTYEAR, 'Stock Transfer - Up To Previous Year': r.STOCKTRANSFER_UPTOPREVIOUSYEAR, 'Stock Transfer - Cumulative': r.STOCKTRANSFER_CUMULATIVE,
            'Difference In Receipts': r.DIFFERENCEINVOICEVALUE, 'Credit Note Issued': r.CreditNote, 'Debit Note Issued': r.DebitNote, 'Bad Debts / Written Off': r.BadDebts,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Project Financial Status');
        XLSX.writeFile(wb, `Project_Financial_Status_Summary_${ranParams?.Fyear || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <LineChart className="h-4 w-4 text-white" />
                            </div>
                            Financial Collection Status Summary Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Project-wise invoice, receipt, TDS and stock transfer status</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Project Financial Status Summary</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Type</label>
                        <div className="flex items-center gap-4 px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg">
                            <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                                <input type="radio" name="ccType" checked={ccType === 'Active CCs'} onChange={() => setCcType('Active CCs')} />Active
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                                <input type="radio" name="ccType" checked={ccType === 'Closed CCs'} onChange={() => setCcType('Closed CCs')} />Closed
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Year</label>
                        <select className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={yearsLoading}>
                            <option value="">-Please Select-</option>
                            {years.map((y, i) => <option key={i} value={y.Year}>{y.YearValue}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No data found.</div>
            ) : rows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">{ranParams.CCType} — {report?.Year || ranParams.Fyear}</h2>
                        <p className="text-[10px] text-red-500 mt-0.5">NOTE: the below report data is not present data — you will get updated data in the next one hour.</p>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom">{ranParams.CCType}</th>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom">Project/Job Description</th>
                                    <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Total Basic Invoice</th>
                                    <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Total Net Client Receipts Incl. TDS</th>
                                    <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">TDS</th>
                                    <th colSpan={3} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Stock Transfer Credits</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400 align-bottom max-w-[140px] whitespace-normal">Difference In Receipts (Cumulative Receipts Incl. TDS − Cumulative Invoice)</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase align-bottom">Credit Note Issued To Client</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase align-bottom">Debit Note Issued By Client</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase align-bottom max-w-[110px] whitespace-normal">Bad Debts / Written off Receivables</th>
                                </tr>
                                <tr className="bg-indigo-700/60">
                                    {['Current Year', 'Up To Previous Year', 'Cumulative', 'Current Year', 'Up To Previous Year', 'Cumulative', 'Current Year', 'Up To Previous Year', 'Cumulative', 'Current Year', 'Up To Previous Year', 'Cumulative'].map((h, i) => (
                                        <th key={i} className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.CCCode}</td>
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.CCName}</td>
                                        <NumCell value={row.BASIC_CURRENTYEAR} /><NumCell value={row.BASIC_UPTOPREVIOUSYEAR} /><NumCell value={row.BASIC_CUMULATIVE} />
                                        <NumCell value={row.NETRECIEPT_CURRENTYEAR} /><NumCell value={row.NETRECIEPT_UPTOPREVIOUSYEAR} /><NumCell value={row.NETRECIEPT_CUMULATIVE} />
                                        <NumCell value={row.TDS_CURRENTYEAR} /><NumCell value={row.TDS_UPTOPREVIOUSYEAR} /><NumCell value={row.TDS_CUMULATIVE} />
                                        <NumCell value={row.STOCKTRANSFER_CURRENTYEAR} /><NumCell value={row.STOCKTRANSFER_UPTOPREVIOUSYEAR} /><NumCell value={row.STOCKTRANSFER_CUMULATIVE} />
                                        <NumCell value={row.DIFFERENCEINVOICEVALUE} /><NumCell value={row.CreditNote} /><NumCell value={row.DebitNote} /><NumCell value={row.BadDebts} />
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" /><td className="px-2 py-1 text-xs">TOTAL</td>
                                    <NumCell value={totals.BASIC_CURRENTYEAR} /><NumCell value={totals.BASIC_UPTOPREVIOUSYEAR} /><NumCell value={totals.BASIC_CUMULATIVE} />
                                    <NumCell value={totals.NETRECIEPT_CURRENTYEAR} /><NumCell value={totals.NETRECIEPT_UPTOPREVIOUSYEAR} /><NumCell value={totals.NETRECIEPT_CUMULATIVE} />
                                    <NumCell value={totals.TDS_CURRENTYEAR} /><NumCell value={totals.TDS_UPTOPREVIOUSYEAR} /><NumCell value={totals.TDS_CUMULATIVE} />
                                    <NumCell value={totals.STOCKTRANSFER_CURRENTYEAR} /><NumCell value={totals.STOCKTRANSFER_UPTOPREVIOUSYEAR} /><NumCell value={totals.STOCKTRANSFER_CUMULATIVE} />
                                    <NumCell value={totals.DIFFERENCEINVOICEVALUE} /><NumCell value={totals.CreditNote} /><NumCell value={totals.DebitNote} /><NumCell value={totals.BadDebts} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ProjectFinancialStatusSummary;
